/**
 * Lead Edit Component
 *
 * Tabbed editor covering the full legacy form: personal data with catalog
 * references, contact channels, address, CRM data (status, consent, contact
 * dates), tags, interests and notes. Duplicate emails warn but never block
 * (legacy behaviour).
 */

import React from 'react';
import {
  BooleanInput,
  DateInput,
  Edit,
  TabbedForm,
  TextInput,
  TopToolbar,
  ShowButton,
  useTranslate,
  required,
  maxLength,
  email,
} from 'react-admin';
import { Box, Grid, Typography } from '@mui/material';
import { EditRounded as EditIcon } from '@mui/icons-material';
import { MyToolbar } from '@/components/forms';
import { HelpTooltip } from '@/components/common/HelpTooltip';
import { httpClient } from '@/utils/httpClient';
import {
  LookupAutocompleteInput,
  LookupAutocompleteArrayInput,
  StatusInput,
} from './LeadInputs';

interface LeadFormData extends Record<string, unknown> {
  id?: number;
  email?: string | null;
}

interface LeadSearchHit {
  id: number;
  email?: string | null;
  secondaryEmail?: string | null;
  firstName?: string;
  lastName?: string;
}

/**
 * Soft duplicate-email check: ask for confirmation, never block silently.
 */
export const confirmDuplicateEmail = async (
  data: LeadFormData,
  warningTemplate: string
): Promise<LeadFormData> => {
  const value = (data.email ?? '').toString().trim().toLowerCase();
  if (!value) return data;

  try {
    const response = await httpClient.get<{ data: LeadSearchHit[] }>(
      `/leads?email=${encodeURIComponent(value)}&includeArchived=true&pageSize=10`
    );
    const duplicate = (response.data.data ?? []).find(
      (hit) =>
        hit.id !== data.id &&
        (hit.email?.toLowerCase() === value || hit.secondaryEmail?.toLowerCase() === value)
    );
    if (duplicate) {
      const who = `${duplicate.firstName ?? ''} ${duplicate.lastName ?? ''}`.trim();
      if (!window.confirm(warningTemplate.replace('%{name}', who || value))) {
        throw new Error('duplicate-email-cancelled');
      }
    }
  } catch (error) {
    if (error instanceof Error && error.message === 'duplicate-email-cancelled') {
      throw error;
    }
    // Lookup failures must never prevent saving.
  }
  return data;
};

const EditActions: React.FC = () => {
  const translate = useTranslate();

  return (
    <TopToolbar sx={{ justifyContent: 'space-between', alignItems: 'center', minHeight: '64px', px: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', color: 'primary.main', fontSize: '1.75rem' }}>
          <EditIcon fontSize="inherit" />
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {translate('resources.leads.edit.title')}
        </Typography>
        <HelpTooltip title={translate('resources.leads.help.editBanner')} />
      </Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <ShowButton variant="contained" />
      </Box>
    </TopToolbar>
  );
};

const Half = ({ children }: { children: React.ReactNode }) => (
  <Grid item xs={12} md={6}>{children}</Grid>
);

export const LeadEdit = () => {
  const translate = useTranslate();
  const f = (key: string) => translate(`resources.leads.fields.${key}`);

  return (
    <Edit
      mutationMode="pessimistic"
      redirect="show"
      actions={<EditActions />}
      transform={(data) =>
        confirmDuplicateEmail(
          data as LeadFormData,
          translate('resources.leads.messages.duplicateEmail')
        )
      }
    >
      <TabbedForm toolbar={<MyToolbar resource="leads" />}>
        <TabbedForm.Tab label={translate('resources.leads.tabs.personal')}>
          <Grid container spacing={2}>
            <Half><TextInput source="treatment" label={f('treatment')} validate={maxLength(50)} fullWidth helperText={false} /></Half>
            <Half><TextInput source="firstName" label={f('firstName')} validate={[required(), maxLength(200)]} fullWidth helperText={false} /></Half>
            <Half><TextInput source="lastName" label={f('lastName')} validate={maxLength(200)} fullWidth helperText={false} /></Half>
            <Half><LookupAutocompleteInput source="companyId" resource="companies" label={f('companyName')} /></Half>
            <Half><LookupAutocompleteInput source="hotelChainId" resource="hotel-chains" label={f('hotelChainName')} /></Half>
            <Half><LookupAutocompleteInput source="hotelId" resource="hotels" label={f('hotelName')} /></Half>
            <Half><LookupAutocompleteInput source="jobTitleId" resource="job-titles" label={f('jobTitleName')} /></Half>
          </Grid>
        </TabbedForm.Tab>

        <TabbedForm.Tab label={translate('resources.leads.tabs.contact')}>
          <Grid container spacing={2}>
            <Half><TextInput source="email" label={f('email')} validate={email()} fullWidth helperText={false} /></Half>
            <Half><TextInput source="secondaryEmail" label={f('secondaryEmail')} validate={email()} fullWidth helperText={false} /></Half>
            <Half><TextInput source="mobilePhone" label={f('mobilePhone')} fullWidth helperText={false} /></Half>
            <Half><TextInput source="personalMobilePhone" label={f('personalMobilePhone')} fullWidth helperText={false} /></Half>
            <Half><TextInput source="landlinePhone" label={f('landlinePhone')} fullWidth helperText={false} /></Half>
            <Half><TextInput source="phoneExtension" label={f('phoneExtension')} fullWidth helperText={false} /></Half>
            <Half><TextInput source="linkedInUrl" label={f('linkedInUrl')} fullWidth helperText={false} /></Half>
            <Half><TextInput source="website" label={f('website')} fullWidth helperText={false} /></Half>
          </Grid>
        </TabbedForm.Tab>

        <TabbedForm.Tab label={translate('resources.leads.tabs.address')}>
          <Grid container spacing={2}>
            <Grid item xs={12}><TextInput source="address" label={f('address')} fullWidth helperText={false} /></Grid>
            <Half><TextInput source="city" label={f('city')} fullWidth helperText={false} /></Half>
            <Half><TextInput source="region" label={f('region')} fullWidth helperText={false} /></Half>
            <Half><TextInput source="country" label={f('country')} fullWidth helperText={false} /></Half>
          </Grid>
        </TabbedForm.Tab>

        <TabbedForm.Tab label={translate('resources.leads.tabs.crm')}>
          <Grid container spacing={2}>
            <Half><LookupAutocompleteInput source="contactTypeId" resource="contact-types" label={f('contactTypeName')} /></Half>
            <Half><LookupAutocompleteInput source="leadSourceId" resource="lead-sources" label={f('leadSourceName')} /></Half>
            <Half><StatusInput source="status" label={f('status')} /></Half>
            <Half><TextInput source="preferredLanguage" label={f('preferredLanguage')} validate={maxLength(20)} fullWidth helperText={false} /></Half>
            <Half><DateInput source="lastContactDate" label={f('lastContactDate')} fullWidth helperText={false} /></Half>
            <Half><DateInput source="nextContactDate" label={f('nextContactDate')} fullWidth helperText={false} /></Half>
            <Half><BooleanInput source="acceptsNewsletter" label={f('acceptsNewsletter')} helperText={false} /></Half>
            <Half><DateInput source="consentDate" label={f('consentDate')} fullWidth helperText={false} /></Half>
            <Half><TextInput source="consentOrigin" label={f('consentOrigin')} validate={maxLength(200)} fullWidth helperText={false} /></Half>
          </Grid>
        </TabbedForm.Tab>

        <TabbedForm.Tab label={translate('resources.leads.tabs.tags')}>
          <LookupAutocompleteArrayInput source="tagIds" resource="tags" label={f('tagNames')} />
        </TabbedForm.Tab>

        <TabbedForm.Tab label={translate('resources.leads.tabs.interests')}>
          <LookupAutocompleteArrayInput source="interestIds" resource="interests" label={f('interestNames')} />
        </TabbedForm.Tab>

        <TabbedForm.Tab label={translate('resources.leads.tabs.notes')}>
          <TextInput source="notes" label={f('notes')} multiline rows={8} fullWidth helperText={false} />
        </TabbedForm.Tab>
      </TabbedForm>
    </Edit>
  );
};

export default LeadEdit;
